import { getCookie, setCookie } from '@dx-ui/utilities-cookies';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import { addDays, startOfToday } from 'date-fns';
import type { IncomingMessage } from 'http';
import { isServer } from '@tanstack/react-query';
import type { GetServerSidePropsContext, NextPageContext } from 'next';
import type { DXError } from '@dx-ui/types-graphql';
import { ServerAuthError, GraphError } from '@dx-ui/types-graphql';
import type { SupportedLanguage } from '@dx-ui/framework-i18n';
import { REGIONALIZED_LOCALE_MAP } from '@dx-ui/framework-i18n';

export function causedByUnauthorized(error?: DXError) {
  if (error) {
    if (error instanceof ServerAuthError) {
      return error.statusCode === 401;
    }
    if (error instanceof GraphError) {
      return (
        error.graphQLErrors?.some(
          (error) => error?.message === 'Unauthorized' || error?.code === 401
        ) || false
      );
    }
  }
  return false;
}

export function causedByForbidden(error?: DXError) {
  if (error) {
    if (error instanceof ServerAuthError) {
      return error.statusCode === 403;
    }
    if (error instanceof GraphError) {
      return (
        error.graphQLErrors?.some(
          (error) => error?.message === 'Forbidden' || error?.code === 403
        ) || false
      );
    }
  }
  return false;
}

export function causedByNotFound(error?: DXError) {
  if (error) {
    if (error instanceof ServerAuthError) {
      return error.statusCode === 404;
    }
    if (error instanceof GraphError) {
      return (
        error.graphQLErrors?.some(
          (error) => error?.message === 'Not Found' || error?.code === 404
        ) || false
      );
    }
  }
  return false;
}

export function getVisitorId() {
  if (!isBrowser) {
    return null;
  }
  const visitorId: string = getCookie('visitorId');
  return visitorId || setVisitorId();
}

export function setVisitorId() {
  if (isBrowser) {
    const uuid = crypto.randomUUID();
    setCookie('visitorId', uuid, {
      expires: addDays(startOfToday(), 90),
      domain: window.location.hostname,
    });
    return uuid;
  }
  return null;
}

export function generateReferrer(origin: string, language: string, request?: IncomingMessage) {
  try {
    const pathname = isServer ? `/${language}${request?.url}` : window.location.pathname;
    const url = new URL(pathname, origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return undefined;
  }
}

/**
 * Maps an 'unsafe' language code or full locale to a SupportedLanguage.
 *
 * Regionalized locales listed in REGIONALIZED_LOCALE_MAP (e.g. en-gb, zh-cn)
 * are preserved as their canonical form. All other hyphenated locales fall back
 * to their base language code (e.g. es-ES -> es).
 *
 * @param language - The language code to get the safe language from
 * @returns The safe language.
 */
export function getSafeLanguage(language: string): SupportedLanguage {
  const lowerCasedLanguage = language.toLowerCase();
  if (REGIONALIZED_LOCALE_MAP[lowerCasedLanguage]) {
    return REGIONALIZED_LOCALE_MAP[lowerCasedLanguage];
  }
  const baseLanguageCode = lowerCasedLanguage.split('-')[0];
  return (baseLanguageCode ?? 'en') as SupportedLanguage;
}

/**
 * This function is used to get the language from the "content" param from requests
 * that were proxied through OneLink.
 *
 * @note This accounts for full locales but always maps to a SupportedLanguage.
 *
 * @param context - The context of the request.
 * @returns The safe language.
 */
export function getServerSideSafeLanguage(
  context: GetServerSidePropsContext | NextPageContext
): SupportedLanguage {
  const queryContentLocale = Array.isArray(context.query.content)
    ? context.query.content[0]
    : context.query.content;
  const language = queryContentLocale || context.locale || 'en';
  return getSafeLanguage(language);
}
